<template>
  <ScrollTop v-if="showScrollTop" />
  <div class="englobe-params">
    <div class="params-row top">
      <FiltersMenu
        :active-filters="activeFilters"
        :is-loading="!store.filters"
        @remove-filter="removeFilterItem"
        @reset-all-filters="resetAllFilters"
      >
        <SearchAsYouTypeFilter
          v-if="!store.isFollawedActive"
          filter="author"
          :items="store.authors"
          item-label="name"
          item-value="_id"
          :active-filters="selectedFilters.author"
          :is-loading="store.isAuthorFilterLoading"
          @search="fetchSearchAuthors"
          @add-filter-item="addFilterItem"
          @remove-filter-item="removeFilterItem"
          @reset-filter="resetAuthorFilter"
        >
          Auteurs
        </SearchAsYouTypeFilter>
        <SelectMultipleCounter
          v-for="(value, key) in filtersNames"
          :key="key"
          :filter="key"
          :items="store.filters[key]"
          :active-filters="selectedFilters[key]"
          has-search
          @add-filter-item="addFilterItem"
          @remove-filter-item="removeFilterItem"
          @reset-filter="resetFilter"
        >
          {{ value }}
        </SelectMultipleCounter>
      </FiltersMenu>

      <FollawedRadioButtons
        :all-match-route="matchedRoute"
        follawed-route="/legislatives/follawed"
      />
    </div>

    <div class="params-row">
      <RadioButtons
        :active-option="activeNatureOptionValue"
        :options="natureOptions"
        @select-option="handleOnNatureChange"
      />
    </div>

    <div class="params-row">
      <SwitchSlide :is-active="!store.bypassQuery" @toggle="toggleUseRequest"
        >Utiliser la requête</SwitchSlide
      >
    </div>

    <div class="params-row date-filters">
      <DropdownMenu
        :model-value="dateFilterTypeLabel"
        :options="['Date de mise à jour', 'Date de dépôt']"
        label="Filtrer par :"
        @update:model-value="handleDateFilterTypeChange"
      />
      <div class="date-inputs">
        <div class="date-input">
          <label>Du</label>
          <input
            type="date"
            :value="store.dateStart"
            :max="store.dateEnd || today"
            @input="handleDateStartChange($event.target.value)"
          />
        </div>
        <div class="date-input">
          <label>Au</label>
          <input
            type="date"
            :value="store.dateEnd"
            :min="store.dateStart"
            :max="today"
            @input="handleDateEndChange($event.target.value)"
          />
        </div>
        <button
          v-if="store.dateStart || store.dateEnd"
          class="date-reset"
          @click="resetDateFilters"
        >
          <i class="fa-solid fa-xmark" />
        </button>
      </div>
    </div>

    <div class="params-row">
      <TotalMatched :item-count="store.item_count" feminine />

      <DropdownMenu
        v-model="selectedLimitOption"
        :options="limitOptions"
        label="Textes par page :"
      />
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { storeToRefs } from "pinia";
import { useRoute } from "vue-router";
import dayjs from "dayjs";
import DropdownMenu from "@follaw-fr/components/reusable/atoms/DropdownMenu.vue";
import FiltersMenu from "@follaw-fr/components/filter-menu/organisms/FiltersMenu.vue";

import ScrollTop from "@follaw-fr/components/reusable/atoms/ScrollTop.vue";
import SearchAsYouTypeFilter from "@follaw-fr/components/filter-menu/molecules/SearchAsYouTypeFilter.vue";
import SelectMultipleCounter from "@follaw-fr/components/filter-menu/molecules/SelectMultipleCounter.vue";

import FollawedRadioButtons from "@/follaw-fr/components/reusable/atoms/FollawedRouterRadioButtons.vue";
import SwitchSlide from "@follaw-fr/components/reusable/atoms/SwitchSlide.vue";
import TotalMatched from "@follaw-fr/components/reusable/atoms/TotalMatched.vue";
import { useDropdown } from "@follaw-fr/composables/useDropdown";
import { useLegiWorkStore } from "@follaw-fr/stores/legi-work";
import RadioButtons from "../../reusable/atoms/RadioButtons.vue";

// All filters in wanted display order with their labels
const filtersNames = {
  current_institution: "Institution",
  current_etape: "Étape",
};
const store = useLegiWorkStore();
const { selectedFilters, legiFilters } = storeToRefs(store);

const today = computed(() => dayjs().format("YYYY-MM-DD"));

const dateFilterTypeLabel = computed(() =>
  store.dateFilterType === "depot_initial"
    ? "Date de dépôt"
    : "Date de mise à jour"
);

const handleDateFilterTypeChange = (label) => {
  store.dateFilterType =
    label === "Date de dépôt" ? "depot_initial" : "updated_at";
  if (store.dateStart || store.dateEnd) {
    fetchLegiWorkList();
  }
};

const handleDateStartChange = (value) => {
  store.dateStart = value || null;
  fetchLegiWorkList();
};

const handleDateEndChange = (value) => {
  store.dateEnd = value || null;
  fetchLegiWorkList();
};

const resetDateFilters = () => {
  store.dateStart = null;
  store.dateEnd = null;
  fetchLegiWorkList();
};

// Handle filter "Nature"
const natureOptions = computed(() =>
  store.filters?.nature.map((item) => ({ value: item, label: item }))
);

const handleOnNatureChange = (nature) => {
  selectedFilters.value = {
    ...selectedFilters.value,
    nature: [nature],
  };
  fetchLegiWorkList();
};

const activeNatureOptionValue = computed(() => {
  const selectedNatureOption = natureOptions.value?.find((item) =>
    legiFilters.value?.nature.includes(item.value)
  );
  return selectedNatureOption?.value;
});

const route = useRoute();

const fetchLegiWorkList = async () => {
  selectedFilters.value.from = 0;
  await store.fetchLegiWorkList();
};

// Returns the route if starts with "/legislatives" and is !"follawed"; otherwise returns the base "/legislatives" route.
const matchedRoute = computed(() => {
  if (
    route.path.startsWith("/legislatives") &&
    !route.fullPath.includes("follawed")
  ) {
    return route.path;
  }
  return "/legislatives";
});

const { options: limitOptions, selectedValue: selectedLimitOption } =
  useDropdown({
    options: { 10: "10", 20: "20", 50: "50", 200: "200" },
    selectedFilters,
    filterKey: "size",
    onChange: () => {
      fetchLegiWorkList();
    },
  });

const resetAllFilters = () => {
  store.resetFilters();
  fetchLegiWorkList();
};

const addFilterItem = (event) => {
  const targetFilters = selectedFilters.value[event.filter] || [];

  const itemToAdd = event.item;

  selectedFilters.value = {
    ...selectedFilters.value,
    [event.filter]: [...targetFilters, itemToAdd],
  };
  fetchLegiWorkList();
};

const showScrollTop = computed(() => selectedFilters.value.size > 20);

const removeFilterItem = (event) => {
  let targetFilters = selectedFilters.value[event.filter] || [];

  targetFilters = targetFilters.filter((el) => el !== event.item);

  if (targetFilters.length === 0) {
    delete selectedFilters.value[event.filter];
  } else {
    selectedFilters.value = {
      ...selectedFilters.value,
      [event.filter]: targetFilters,
    };
  }
  fetchLegiWorkList();
};

const activeFilters = computed(() => {
  let arr = [];
  // Put all selected options from all filters with said filter name in an array for the SelectedFilters component
  Object.keys(filtersNames).forEach((key) => {
    selectedFilters.value[key]?.forEach((filter) => {
      arr = [...arr, { filter: key, item: filter }];
    });
  });
  return arr;
});

const toggleUseRequest = () => {
  store.bypassQuery = !store.bypassQuery;
  store.fetchLegiWorkList();
};

const fetchSearchAuthors = (nameSearch) => {
  if (nameSearch.length > 0) {
    store.getSearchAuthorsFilters(nameSearch);
  } else {
    store.authors = [];
  }
};
const resetAuthorFilter = () => {
  store.authors = [];
  delete selectedFilters.value.author;
  fetchLegiWorkList();
};

const resetFilter = (event) => {
  delete selectedFilters.value[event];
  fetchLegiWorkList();
};
</script>

<style lang="scss" scoped>
.date-filters {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.date-inputs {
  display: flex;
  align-items: center;
  gap: 8px;
}

.date-input {
  display: flex;
  align-items: center;
  gap: 4px;

  label {
    font-size: var(--size-dynamic-medium);
    white-space: nowrap;
  }

  input[type="date"] {
    background: transparent;
    border: 1px solid var(--clr-n4);
    border-radius: 7px;
    color: var(--clr-n8);
    padding: 4px 8px;
    height: 32px;
    cursor: pointer;
    font-size: var(--size-dynamic-medium);
  }
}

.date-reset {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: var(--clr-n3);
  cursor: pointer;

  &:hover {
    background-color: var(--clr-n4);
  }
}
</style>
